import { assert } from '@common/errors';
import { Goal } from '@common/goals';
import { CurrentRateInfo } from '@common/graphql/generated';
import { useGoal } from '@common/react/financialEntities';
import { useCurrentRateInfo } from '@common/react/rates';
import { createContext, useContext, useEffect, useState } from 'react';

export type GoalDetailPosition =
    | 'DETAILS'
    | 'PARTNER-ONBOARDING'
    | 'LOADING'
    | 'ONBOARDING'
    | 'CELEBRATION';

interface GoalDetailContext {
    goalId: string;
    goal?: Goal;
    goalUnfiltered?: Goal;
    goalLoading: boolean;
    currentRateInfo: CurrentRateInfo;
    onClose: () => void;
    position: GoalDetailPosition;
    setPosition: (position: GoalDetailPosition) => void;
    isGoalStatementPopoverOpen: boolean;
    setIsGoalStatementPopoverOpen: (open: boolean) => void;
}

const Ctx = createContext<GoalDetailContext | null>(null);

export const GoalDetailProvider = ({
    id,
    onClose,
    children,
}: {
    id: string;
    onClose: () => void;
    children: React.ReactNode;
}) => {
    const { currentRateInfo } = useCurrentRateInfo();
    const goalRes = useGoal({ id });

    const [position, setPosition] = useState<GoalDetailPosition>(
        initialPosition(goalRes.unfilteredGoal)
    );
    useEffect(() => {
        if (position === 'LOADING' && goalRes.unfilteredGoal) {
            setPosition(initialPosition(goalRes.unfilteredGoal));
        }
    }, [goalRes.unfilteredGoal, position]);

    const [isGoalStatementPopoverOpen, setIsGoalStatementPopoverOpen] =
        useState(false);

    if (!currentRateInfo) {
        return <></>;
    }

    return (
        <Ctx.Provider
            value={{
                goalId: id,
                goal: goalRes.goal,
                goalUnfiltered: goalRes.unfilteredGoal,
                goalLoading: goalRes.loading,
                currentRateInfo,
                onClose,
                position,
                setPosition,
                isGoalStatementPopoverOpen,
                setIsGoalStatementPopoverOpen,
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

const initialPosition = (goal?: Goal): GoalDetailPosition => {
    if (!goal) return 'LOADING';
    if (goal.type === 'RETIREMENT') return 'DETAILS';
    if (goal.financialEntities.length === 0) return 'ONBOARDING';
    return 'DETAILS';
};

export const useGoalDetailContext = (): GoalDetailContext => {
    const ctx = useContext(Ctx);
    assert(ctx);
    return ctx;
};
